import React, { useContext, useEffect, useRef, useState } from "react";
import { Row, Col, Modal } from "react-bootstrap";
import { Search, Eye, Trash2, Layers, RefreshCw, X } from "lucide-react";
import axios from "axios";
import AppContext from "../../../context/AppContext";
import "../../../assets/css/gcstudent/flashcardpage/FlashCardPage.css";
import Pagination from "../../../components/general/pagination/Pagination";
import Loader from "../../../components/general/loader/Loader";

/* ── Format descryption text (bold markdown) ── */
const formatDesc = (text) => {
  if (!text) return null;
  return text.split("\n").map((line, i) => {
    const parts = line.split(/\*\*(.*?)\*\*/g);
    return (
      <p key={i} className="gcstudent-fc-modal__back-line">
        {parts.map((part, j) =>
          j % 2 === 1 ? <strong key={j}>{part}</strong> : part
        )}
      </p>
    );
  });
};

/* ── Format date ── */
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
};

/* ══════════════════════════════════════
   Flash Card Modal
══════════════════════════════════════ */
const FlashCardModal = ({ card, show, onClose }) => {
  const [flipped, setFlipped] = useState(false);

  React.useEffect(() => {
    if (show) setFlipped(false);
  }, [show, card?.id]);

  if (!card) return null;

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      className="gcstudent-fc-modal-wrapper"
      backdropClassName="gcstudent-fc-backdrop"
    >
      <div className="gcstudent-fc-modal">

        {/* Header */}
        <div className="gcstudent-fc-modal__header">
          <div>
            <h5 className="gcstudent-fc-modal__title">{card.title}</h5>
            {card.subtitle && (
              <p className="gcstudent-fc-modal__source">{card.subtitle}</p>
            )}
          </div>
          <button className="gcstudent-fc-modal__close" onClick={onClose}>
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        {/* Card flip area */}
        <div className="gcstudent-fc-modal__scene">
          <div className={`gcstudent-fc-modal__card ${flipped ? "gcstudent-fc-modal__card--flipped" : ""}`}>

            {/* Front face — title */}
            <div className="gcstudent-fc-modal__face gcstudent-fc-modal__face--front">
              <Layers size={36} strokeWidth={1.4} className="gcstudent-fc-modal__face-icon" />
              <h6 className="gcstudent-fc-modal__face-title">{card.title}</h6>
              <p className="gcstudent-fc-modal__face-hint">Click to reveal content</p>
            </div>

            {/* Back face — descryption */}
            <div className="gcstudent-fc-modal__face gcstudent-fc-modal__face--back">
              <div className="gcstudent-fc-modal__back-content">
                {formatDesc(card.descryption)}
              </div>
            </div>
          </div>
        </div>

        {/* Flip button */}
        <div className="gcstudent-fc-modal__footer">
          <button
            className="gcstudent-fc-modal__flip-btn"
            onClick={() => setFlipped((prev) => !prev)}
          >
            <RefreshCw
              size={15}
              strokeWidth={2.2}
              className={flipped ? "gcstudent-fc-spin" : ""}
            />
            Flip Card
          </button>
        </div>

      </div>
    </Modal>
  );
};

/* ══════════════════════════════════════
   Flash Cards Page
══════════════════════════════════════ */
const ITEMS_PER_PAGE = 9; // yahan se page size control hoti hai (3-column grid, isliye 9)

const FlashCardPage = () => {
  const { baseUrl, authToken, loggedInUser } = useContext(AppContext);

  const [searchQuery, setSearchQuery]   = useState("");
  const [cards, setCards]               = useState([]);
  const [loading, setLoading]           = useState(true);   // initial API fetch loading
  const [viewLoading, setViewLoading]   = useState(false);  // page switch / search loading (sirf visible cards k liye)
  const [selectedCard, setSelectedCard] = useState(null);
  const [showModal, setShowModal]       = useState(false);
  const [currentPage, setCurrentPage]   = useState(1); // pagination state

  // Pehli render (initial mount) par view-loading trigger na ho, is liye ref se track kar rahe hain
  const isFirstRender = useRef(true);

  /* ── Fetch flash cards ── */
  useEffect(() => {
    if (!loggedInUser?.id) { setLoading(false); return; }

    const formData = new FormData();
    formData.append("lbstudent_id", loggedInUser.id);

    axios
      .post(`${baseUrl}/student/flashcards/fetchAllFlashCards`, formData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "multipart/form-data",
        },
      })
      .then((res) => {
        if (res.data.success) {
          setCards(res.data.flashCards ?? []);
        }
      })
      .catch((err) => console.error("Failed to fetch flash cards:", err))
      .finally(() => setLoading(false));
  }, [baseUrl, authToken, loggedInUser]);

  /* ── Delete flash card ── */
  const handleDelete = (id, e) => {
    e.stopPropagation();

    const formData = new FormData();
    formData.append("id", id);
    formData.append("lbstudent_id", loggedInUser.id);

    axios
      .post(`${baseUrl}/student/flashcards/deleteFlashCard`, formData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "multipart/form-data",
        },
      })
      .then((res) => {
        if (res.data.success) {
          setCards((prev) => prev.filter((c) => c.id !== id));
        }
      })
      .catch((err) => console.error("Failed to delete flash card:", err));
  };

  const handleView  = (card) => { setSelectedCard(card); setShowModal(true); };
  const handleClose = () => { setShowModal(false); setTimeout(() => setSelectedCard(null), 300); };

  const filtered = cards.filter(
    (c) =>
      c.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.subtitle?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  /* ── Pagination slice (sirf current page ka data grid mein jayega) ── */
  const paginatedCards = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  /* ── Search change hone par page 1 pe reset ── */
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  /* ── Sirf visible cards ki loading dikhayein:
     - Jab search se list filter ho
     - Jab pagination se dusre page par jayen (e.g. page 1 k 9 cards dikhane
       k baad page 2 pe jayen tu sirf 10-18 wale cards ki loading chale)
     Poora grid ek sath "pop" nahi hoga. ── */
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setViewLoading(true);
    const timer = setTimeout(() => setViewLoading(false), 400);
    return () => clearTimeout(timer);
  }, [currentPage, searchQuery]);

  return (
    <div className="gcstudent-flashcards">

      {/* ── Header ── */}
      <div className="gcstudent-flashcards__header">
        <h2>My Flash Cards</h2>
        <p>Quick study cards created from your notes and PDFs using AI.</p>
      </div>

      {/* ── Search ── */}
      <div className="gcstudent-flashcards__search-row">
        <div className="gcstudent-flashcards__search-wrap">
          <input
            type="text"
            className="gcstudent-flashcards__search-input"
            placeholder="Search flash cards..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <button className="gcstudent-flashcards__search-btn">
          <Search size={15} strokeWidth={2.2} />
        </button>
      </div>

      {/* ── Initial Loading (poora page pehli baar load ho raha hai) ── */}
      {loading && <Loader message="Flash cards loading, Please wait..." />}

      {/* ── Cards Grid ── */}
      {!loading && (
        <>
          <Row className="g-3 mt-1">
            {viewLoading ? (
              /* ── Page switch / search loading — sirf is section ki, baaki page affect nahi hota ── */
              <Col xs={12}>
                <Loader message="Loading flash cards..." />
              </Col>
            ) : (
              <>
                {paginatedCards.map((card, i) => (
                  <Col xs={12} sm={6} lg={4} key={card.id}>
                    <div
                      className="gcstudent-fc-card"
                      style={{ "--card-index": i }}
                      onClick={() => handleView(card)}
                    >
                      {/* Top */}
                      <div className="gcstudent-fc-card__top">
                        <div className="gcstudent-fc-card__icon-wrap">
                          <Layers size={20} strokeWidth={1.6} />
                        </div>
                        <div className="gcstudent-fc-card__actions">
                          <button
                            className="gcstudent-fc-card__action-btn gcstudent-fc-card__action-btn--view"
                            onClick={(e) => { e.stopPropagation(); handleView(card); }}
                            title="View"
                          >
                            <Eye size={15} strokeWidth={2} />
                          </button>
                          <button
                            className="gcstudent-fc-card__action-btn gcstudent-fc-card__action-btn--delete"
                            onClick={(e) => handleDelete(card.id, e)}
                            title="Delete"
                          >
                            <Trash2 size={15} strokeWidth={2} />
                          </button>
                        </div>
                      </div>

                      {/* Info */}
                      <div className="gcstudent-fc-card__info">
                        <h6 className="gcstudent-fc-card__title">{card.title}</h6>
                        {card.subtitle && (
                          <p className="gcstudent-fc-card__source">{card.subtitle}</p>
                        )}
                      </div>

                      {/* Bottom: type + date */}
                      <div className="gcstudent-fc-card__bottom">
                        <span className={`gcstudent-fc-tag gcstudent-fc-tag--${card.type?.toLowerCase()}`}>
                          {card.type}
                        </span>
                        <span className="gcstudent-fc-card__date">{formatDate(card.created_at)}</span>
                      </div>
                    </div>
                  </Col>
                ))}

                {filtered.length === 0 && (
                  <Col xs={12}>
                    <div className="gcstudent-flashcards__empty">
                      <Layers size={38} strokeWidth={1.2} />
                      <p>No flash cards found.</p>
                    </div>
                  </Col>
                )}
              </>
            )}
          </Row>

          {/* ── Pagination (view-loading k dauran bhi visible rehti hai) ── */}
          <Pagination
            currentPage={currentPage}
            totalItems={filtered.length}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      {/* ── Modal ── */}
      <FlashCardModal card={selectedCard} show={showModal} onClose={handleClose} />
    </div>
  );
};

export default FlashCardPage;